import { gql, useLazyQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { Autocomplete } from '@flixbus/honeycomb-react';

const SEARCH_MOBILITY_PARTNERS = gql`
	query searchMobilityPartnersByShortName($shortName: String!) {
		searchMobilityPartnersByShortName(shortName: $shortName) {
			id
			shortName
		}
	}
`;

const MobilityPartnerFieldMulti = ({
	value = [],
	onSelection = () => {},
	onReset = () => {},
	onSingleItemReset = () => {},
	extraClasses = '',
}) => {
	const [inputValue, setInputValue] = useState('');

	const [getMobilityPartners, { loading }] = useLazyQuery(SEARCH_MOBILITY_PARTNERS);

	const selectedOptions = useMemo(() => {
		return value.map((item) => ({
			title: item.name,
			value: item.id,
		}));
	}, [value]);

	const handleInputChange = (event) => {
		const nextValue = event?.target?.value ?? '';
		setInputValue(nextValue);
	};

	const handleDebounce = async (typedValue) => {
		if (!typedValue) return [];

		try {
			const { data } = await getMobilityPartners({
				variables: { shortName: typedValue },
			});

			return (data?.searchMobilityPartnersByShortName ?? []).map((partner) => ({
				title: partner.shortName,
				value: partner.id,
			}));
		} catch {
			return [];
		}
	};

	const handleAutocompleteChange = (selectedOptions) => {
		const nextSelected = Array.isArray(selectedOptions) ? selectedOptions : [];

		const previousSelectedIds = value.map((item) => item.id);
		const nextSelectedIds = nextSelected.map((item) => item.value);

		if (nextSelectedIds.length > previousSelectedIds.length) {
			const addedOption = nextSelected.find((item) => !previousSelectedIds.includes(item.value));

			if (addedOption) {
				onSelection({
					id: addedOption.value,
					name: addedOption.title,
				});
				setInputValue('');
			}

			return;
		}

		if (nextSelectedIds.length < previousSelectedIds.length) {
			if (nextSelectedIds.length === 0) {
				onReset();
				return;
			}

			const removedItem = value.find((item) => !nextSelectedIds.includes(item.id));

			if (removedItem) {
				onSingleItemReset(removedItem);
			}
		}
	};

	return (
		<Autocomplete
			multiple
			value={selectedOptions}
			inputValue={inputValue}
			options={[]}
			debounce={300}
			onDebounce={handleDebounce}
			onChange={handleAutocompleteChange}
			comboboxProps={{
				loading,
				label: 'Mobility partner',
				placeholder: 'e.g Best Partner',
				onChange: handleInputChange,
				limitSelectedOptions: 3,
				tagProps: (selectedOption) => ({
					closeProps: {
						label: `Remove ${selectedOption.title}`,
					},
				}),
			}}
			listBoxProps={{
				label: 'partners',
				optionsToDisplay: 5,
			}}
			extraClasses={extraClasses}
		/>
	);
};

export default MobilityPartnerFieldMulti;
